import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button
} from '@mui/material';
import { Home, Chat, Settings, BarChart, People, SmartToy, Bolt, Code, ExpandMore, NavigateNext, CheckCircle, Info, Lightbulb, ArrowBack, Architecture as ArchitectureIcon, Security, Storage, Memory, Cloud } from '@mui/icons-material';

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState('getting-started');

  const sections: HelpSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Home />,
      content: <GettingStartedContent />
    },
    {
      id: 'architecture',
      title: 'System Architecture',
      icon: <ArchitectureIcon />,
      content: <ArchitectureContent />
    },
    {
      id: 'conversations',
      title: 'Conversations',
      icon: <Chat />,
      content: <ConversationsContent />
    },
    {
      id: 'channels',
      title: 'Channels',
      icon: <Settings />,
      content: <ChannelsContent />
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      icon: <SmartToy />,
      content: <AIFeaturesContent />
    },
    {
      id: 'automation',
      title: 'Automation',
      icon: <Bolt />,
      content: <AutomationContent />
    },
    {
      id: 'reports',
      title: 'Reports & Analytics',
      icon: <BarChart />,
      content: <ReportsContent />
    },
    {
      id: 'teams',
      title: 'Team Management',
      icon: <People />,
      content: <TeamsContent />
    },
    {
      id: 'api',
      title: 'API & Integrations',
      icon: <Code />,
      content: <APIContent />
    }
  ];

  const currentSection = sections.find(s => s.id === selectedSection);

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Converse Help Center
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Everything you need to know about using Converse AI Platform
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Sidebar Navigation */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ position: 'sticky', top: 20 }}>
              <List>
                {sections.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    <ListItemButton
                      selected={selectedSection === section.id}
                      onClick={() => setSelectedSection(section.id)}
                    >
                      <ListItemIcon sx={{ color: selectedSection === section.id ? 'primary.main' : 'inherit' }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText primary={section.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 4 }}>
              {/* Back Button */}
              <Box sx={{ mb: 2 }}>
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/dashboard/settings')}
                  sx={{ textTransform: 'none' }}
                >
                  Back to Settings
                </Button>
              </Box>

              {/* Breadcrumbs */}
              <Breadcrumbs
                separator={<NavigateNext fontSize="small" />}
                sx={{ mb: 3 }}
              >
                <Link color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }} underline="hover">
                  Dashboard
                </Link>
                <Typography color="text.primary">Help</Typography>
                <Typography color="text.primary">{currentSection?.title}</Typography>
              </Breadcrumbs>

              {/* Content */}
              {currentSection?.content}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// ==================== CONTENT COMPONENTS ====================

const GettingStartedContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Getting Started with Converse
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Welcome to Converse AI Platform - your intelligent customer support solution powered by AI.
    </Typography>

    <Alert severity="info" sx={{ mb: 3 }}>
      <strong>New to Converse?</strong> Follow this guide to set up your first conversation channel in minutes!
    </Alert>

    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">1. Create Your Account</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Sign up at the Converse registration page with your email address. You'll receive a confirmation email to activate your account.
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
            <ListItemText primary="Enter your business details" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
            <ListItemText primary="Verify your email address" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
            <ListItemText primary="Complete your profile setup" />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">2. Set Up Your First Channel</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Channels are how your customers reach you. Converse supports multiple channels:
        </Typography>
        <Grid container spacing={2}>
          {['Website Widget', 'WhatsApp', 'Instagram', 'Facebook', 'Email', 'API'].map((channel) => (
            <Grid item xs={6} sm={4} key={channel}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" align="center">{channel}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography paragraph sx={{ mt: 2 }}>
          Navigate to <strong>Settings → Channels</strong> to add your first channel.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">3. Invite Your Team</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Go to <strong>Settings → Teams → Add Agent</strong> to invite team members.
        </Typography>
        <Typography paragraph>
          Assign roles based on permissions:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Administrator"
              secondary="Full access to all features and settings"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Agent"
              secondary="Handle conversations and access reports"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Viewer"
              secondary="Read-only access to conversations and reports"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">4. Configure AI Settings</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Enable AI-powered features to automate responses and improve efficiency:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
            <ListItemText
              primary="AI Auto-Reply"
              secondary="Automatically respond to common questions"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
            <ListItemText
              primary="AI Co-Pilot"
              secondary="Get AI suggestions while chatting"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
            <ListItemText
              primary="AI Summaries"
              secondary="Automatically summarize conversations"
            />
          </ListItem>
        </List>
        <Typography paragraph>
          Configure at <strong>Settings → AI Configuration</strong>
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Box sx={{ mt: 4, p: 3, bgcolor: '#f0f9ff', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        <Lightbulb sx={{ verticalAlign: 'middle', mr: 1 }} />
        Quick Tips
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="💡 Use keyboard shortcuts to navigate faster (press '?' to see all shortcuts)" />
        </ListItem>
        <ListItem>
          <ListItemText primary="💡 Set up canned responses for frequently asked questions" />
        </ListItem>
        <ListItem>
          <ListItemText primary="💡 Enable desktop notifications to never miss a message" />
        </ListItem>
      </List>
    </Box>
  </Box>
);

const ArchitectureContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Converse.ai System Architecture
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Understanding the technical foundation of the Converse.ai platform - built for scalability, reliability, and performance.
    </Typography>

    <Alert severity="info" sx={{ mb: 3 }}>
      <strong>Enterprise-Grade Architecture:</strong> Converse.ai uses modern microservices architecture with AI-powered capabilities.
    </Alert>

    <Divider sx={{ my: 3 }} />

    <Typography variant="h5" gutterBottom fontWeight="bold">
      Core Components
    </Typography>

    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Code color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Frontend Layer</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Technology:</strong> React 18 + TypeScript + Material-UI
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Modern single-page application with real-time WebSocket connections, responsive design for mobile and desktop, and optimized performance with code splitting.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Cloud color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Backend API</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Technology:</strong> Python FastAPI
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              High-performance async RESTful API with JWT authentication, comprehensive validation, automatic OpenAPI documentation, and rate limiting.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Storage color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Database Layer</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Technology:</strong> PostgreSQL with JSONB
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Relational database with flexible custom attributes support, full-text search capabilities, optimized indexing strategies, and automatic backups.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <SmartToy color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">AI Engine</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Technology:</strong> OpenAI GPT-4 / Custom Models
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Advanced AI capabilities: conversation summarization, sentiment analysis, auto-responses, intelligent routing with customizable prompts and temperature controls.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Memory color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Vector Database</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Technology:</strong> ChromaDB / Qdrant
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Semantic search and knowledge base powered by embedding vectors, enabling intelligent document retrieval and context-aware AI responses.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Security color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Message Queue</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Technology:</strong> Celery + Redis
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Asynchronous task processing for background jobs, email sending, webhook deliveries, and AI processing without blocking the main application thread.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Divider sx={{ my: 4 }} />

    <Typography variant="h5" gutterBottom fontWeight="bold">
      Data Flow Architecture
    </Typography>

    <Box sx={{ bgcolor: '#f5f5f5', p: 3, borderRadius: 2, mt: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
      <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
{`1. Customer Message → Widget/Channel
   ↓
2. Message Ingestion → API Gateway
   ↓
3. Authentication & Validation
   ↓
4. Message Processing:
   • Store in Database
   • Trigger AI Analysis (async)
   • Apply Automation Rules
   • Route to Agent/Team
   ↓
5. Real-time Push → WebSocket
   ↓
6. Agent Response → Same Flow (reverse)
   ↓
7. Delivery → Customer Channel`}
      </Typography>
    </Box>

    <Divider sx={{ my: 4 }} />

    <Typography variant="h5" gutterBottom fontWeight="bold">
      Security Architecture
    </Typography>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={6}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Authentication & Authorization
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="JWT tokens (30-min expiry)" secondary="With refresh token rotation" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Role-Based Access Control" secondary="Granular permissions per resource" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Password hashing" secondary="Bcrypt with salt rounds" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Network & Data Security
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="HTTPS/TLS 1.3" secondary="All API communication encrypted" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Rate Limiting" secondary="Per endpoint and per user" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="CORS Configuration" secondary="Trusted domains only" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Application Security
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="SQL Injection Prevention" secondary="Parameterized queries only" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="XSS Protection" secondary="Content sanitization" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="CSRF Protection" secondary="Token-based validation" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Webhook Security
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Signature Verification" secondary="HMAC SHA-256 signatures" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Timestamp Validation" secondary="Prevent replay attacks" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Retry Logic" secondary="Exponential backoff" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Divider sx={{ my: 4 }} />

    <Typography variant="h5" gutterBottom fontWeight="bold">
      Scalability & Performance
    </Typography>

    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid item xs={12} md={4}>
        <Box>
          <Typography variant="h6" color="primary" gutterBottom>
            Horizontal Scaling
          </Typography>
          <Typography variant="body2">
            • Load-balanced API servers<br />
            • Stateless architecture<br />
            • Database read replicas<br />
            • CDN for static assets
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} md={4}>
        <Box>
          <Typography variant="h6" color="primary" gutterBottom>
            Caching Strategy
          </Typography>
          <Typography variant="body2">
            • Redis for session data<br />
            • API response caching<br />
            • Database query caching<br />
            • Browser caching headers
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} md={4}>
        <Box>
          <Typography variant="h6" color="primary" gutterBottom>
            Monitoring & Logs
          </Typography>
          <Typography variant="body2">
            • Application performance monitoring<br />
            • Error tracking & alerting<br />
            • Structured logging<br />
            • Audit trail for compliance
          </Typography>
        </Box>
      </Grid>
    </Grid>

    <Alert severity="success" sx={{ mt: 4 }}>
      <Typography variant="body2">
        <strong>Production-Ready:</strong> Converse.ai architecture is battle-tested and supports enterprise deployments with 99.9% uptime SLA.
      </Typography>
    </Alert>
  </Box>
);

const ConversationsContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Managing Conversations
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Learn how to efficiently handle customer conversations in Converse.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Typography variant="h5" gutterBottom>Conversation Status</Typography>
    <Typography paragraph>
      Each conversation can have one of the following statuses:
    </Typography>
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Chip label="OPEN" color="success" sx={{ mb: 1 }} />
            <Typography variant="body2">
              Active conversations that need attention
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Chip label="PENDING" color="warning" sx={{ mb: 1 }} />
            <Typography variant="body2">
              Waiting for customer response
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Chip label="RESOLVED" color="info" sx={{ mb: 1 }} />
            <Typography variant="body2">
              Issue has been resolved
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Chip label="SNOOZED" color="default" sx={{ mb: 1 }} />
            <Typography variant="body2">
              Temporarily hidden, returns later
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Key Features</Typography>
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Assigning Conversations</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Assign conversations to specific team members for better organization:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Manual Assignment"
              secondary="Click 'Assign' and select a team member"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Auto Assignment"
              secondary="Enable round-robin or load-based assignment in settings"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Using Labels</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Organize conversations with custom labels:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Create Labels"
              secondary="Go to Settings → Labels to create custom tags"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Apply Labels"
              secondary="Click the label icon in any conversation to tag it"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Filter by Labels"
              secondary="Use the filter dropdown to view conversations by label"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Canned Responses</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Save time with pre-written responses:
        </Typography>
        <Typography paragraph>
          1. Create canned responses at <strong>Settings → Canned Responses</strong><br />
          2. Use shortcodes (e.g., type <code>/hello</code>) in the message box<br />
          3. Customize with variables like {'{customer_name}'}, {'{agent_name}'}
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Private Notes</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Add internal notes that customers cannot see:
        </Typography>
        <Typography paragraph>
          • Click the <strong>Note</strong> tab in the conversation<br />
          • Add comments for your team<br />
          • Perfect for handoff information or context
        </Typography>
      </AccordionDetails>
    </Accordion>
  </Box>
);

const ChannelsContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Channels Configuration
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Connect with your customers across multiple platforms.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📱 Website Widget
            </Typography>
            <Typography paragraph>
              Add a live chat widget to your website in minutes.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Setup Steps:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Go to Settings → Channels → Add Channel → Website" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Customize widget appearance and behavior" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Copy the installation code" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Paste before closing </body> tag on your website" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              💬 WhatsApp
            </Typography>
            <Typography paragraph>
              Connect your WhatsApp Business account.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Setup Steps:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Get WhatsApp Business API access" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Go to Settings → Channels → Add Channel → WhatsApp" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Enter your phone number and API credentials" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Complete verification process" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📸 Instagram
            </Typography>
            <Typography paragraph>
              Handle Instagram Direct Messages in Converse.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Setup Steps:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Ensure your Instagram is a Business/Creator account" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Connect to Facebook Page" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Go to Settings → Channels → Add Channel → Instagram" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Authorize Converse to access your messages" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📧 Email
            </Typography>
            <Typography paragraph>
              Convert emails into conversations.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Setup Steps:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Go to Settings → Channels → Add Channel → Email" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Enter your email forwarding address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Configure IMAP/SMTP settings" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Test connection and start receiving emails" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔌 API Channel
            </Typography>
            <Typography paragraph>
              Build custom integrations using our API.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Use Cases:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• Mobile apps" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Custom messaging platforms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• IoT devices" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Internal tools" />
              </ListItem>
            </List>
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              See API Documentation for integration details →
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📱 SMS
            </Typography>
            <Typography paragraph>
              Handle SMS conversations through Twilio integration.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>Setup Steps:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Create a Twilio account and get credentials" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Purchase a phone number in Twilio" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Add SMS channel in Converse with Twilio credentials" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Configure webhook URL in Twilio dashboard" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Alert severity="info" sx={{ mt: 3 }}>
      <strong>Pro Tip:</strong> You can add multiple channels of the same type (e.g., multiple WhatsApp numbers) for different teams or regions.
    </Alert>
  </Box>
);

const AIFeaturesContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      AI-Powered Features
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Leverage artificial intelligence to enhance customer support efficiency.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">🤖 AI Auto-Reply</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Automatically respond to customer messages using AI.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>How it works:</Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Instant Responses"
              secondary="AI analyzes customer questions and provides relevant answers instantly"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Knowledge Base Integration"
              secondary="Pulls information from your knowledge base articles"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Smart Escalation"
              secondary="Transfers to human agent when AI confidence is low"
            />
          </ListItem>
        </List>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Configuration:</Typography>
        <Typography paragraph>
          Go to <strong>Settings → AI Configuration → Auto-Reply Settings</strong>
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="• Enable/disable per channel" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Set confidence threshold (0-100%)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Configure escalation rules" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Customize AI tone and style" />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">👨‍💻 AI Co-Pilot</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Get AI-powered suggestions while chatting with customers.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>Features:</Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Response Suggestions"
              secondary="AI recommends appropriate responses based on context"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Sentiment Analysis"
              secondary="Understand customer emotions in real-time"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Knowledge Lookup"
              secondary="Quick access to relevant KB articles"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Translation"
              secondary="Communicate in multiple languages seamlessly"
            />
          </ListItem>
        </List>
        <Typography paragraph sx={{ mt: 2 }}>
          Access Co-Pilot by clicking the <strong>🤖 icon</strong> in any conversation.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">📝 AI Summaries</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Automatically generate conversation summaries.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>Benefits:</Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Quick Handoffs"
              secondary="New agents can quickly understand conversation context"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Better Reporting"
              secondary="Extract key insights for analytics"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Time Saving"
              secondary="No need to read entire conversation history"
            />
          </ListItem>
        </List>
        <Typography paragraph sx={{ mt: 2 }}>
          Summaries are generated automatically when conversations are resolved or on-demand via the <strong>Summary</strong> button.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">🎯 Intent Detection</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          AI identifies customer intent automatically.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>Common Intents:</Typography>
        <Grid container spacing={1}>
          {[
            'Product Inquiry', 'Technical Support', 'Billing Question',
            'Feature Request', 'Bug Report', 'Account Issue',
            'Cancellation', 'Refund Request', 'General Question'
          ].map((intent) => (
            <Grid item key={intent}>
              <Chip label={intent} size="small" />
            </Grid>
          ))}
        </Grid>
        <Typography paragraph sx={{ mt: 2 }}>
          Use intents for:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="• Automatic routing to specialized teams" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Priority assignment" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Analytics and reporting" />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Alert severity="success" sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        <strong>AI Model Selection</strong>
      </Typography>
      <Typography variant="body2">
        Converse supports multiple AI providers: OpenAI (GPT-4, GPT-3.5), Anthropic (Claude), and Groq.
        Configure your preferred model in Settings → AI Configuration.
      </Typography>
    </Alert>
  </Box>
);

const AutomationContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Automation Rules
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Automate repetitive tasks and streamline your workflow.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Typography variant="h5" gutterBottom>Creating Automation Rules</Typography>
    <Typography paragraph>
      Go to <strong>Settings → Automation</strong> to create custom rules.
    </Typography>

    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Rule Structure</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Every automation rule consists of:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Trigger"
              secondary="When should this rule run?"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Conditions"
              secondary="What criteria must be met? (Optional)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Actions"
              secondary="What should happen when triggered?"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Available Triggers</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>Conversation Events:</Typography>
            <List dense>
              <ListItem><ListItemText primary="• Conversation created" /></ListItem>
              <ListItem><ListItemText primary="• Message received" /></ListItem>
              <ListItem><ListItemText primary="• Conversation assigned" /></ListItem>
              <ListItem><ListItemText primary="• Status changed" /></ListItem>
            </List>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>Time-Based:</Typography>
            <List dense>
              <ListItem><ListItemText primary="• After X hours/days" /></ListItem>
              <ListItem><ListItemText primary="• Business hours start/end" /></ListItem>
              <ListItem><ListItemText primary="• Scheduled time" /></ListItem>
            </List>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Available Actions</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem><ListItemText primary="• Assign to agent/team" /></ListItem>
              <ListItem><ListItemText primary="• Add label" /></ListItem>
              <ListItem><ListItemText primary="• Send message" /></ListItem>
              <ListItem><ListItemText primary="• Change status" /></ListItem>
              <ListItem><ListItemText primary="• Set priority" /></ListItem>
            </List>
          </Grid>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem><ListItemText primary="• Snooze conversation" /></ListItem>
              <ListItem><ListItemText primary="• Send email notification" /></ListItem>
              <ListItem><ListItemText primary="• Trigger webhook" /></ListItem>
              <ListItem><ListItemText primary="• Run custom script" /></ListItem>
            </List>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Example Use Cases</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Auto-Assignment by Channel:</Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
            <Typography variant="body2">
              <strong>Trigger:</strong> Conversation created<br />
              <strong>Condition:</strong> Channel = WhatsApp<br />
              <strong>Action:</strong> Assign to "WhatsApp Team"
            </Typography>
          </Card>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>After-Hours Response:</Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
            <Typography variant="body2">
              <strong>Trigger:</strong> Message received<br />
              <strong>Condition:</strong> Outside business hours<br />
              <strong>Action:</strong> Send canned response "We'll reply during business hours"
            </Typography>
          </Card>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Auto-Resolve Old Conversations:</Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
            <Typography variant="body2">
              <strong>Trigger:</strong> After 48 hours<br />
              <strong>Condition:</strong> Status = Pending, No customer reply<br />
              <strong>Action:</strong> Mark as Resolved
            </Typography>
          </Card>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Alert severity="warning" sx={{ mt: 3 }}>
      <strong>Important:</strong> Test automation rules thoroughly before enabling them in production to avoid unintended consequences.
    </Alert>
  </Box>
);

const ReportsContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Reports & Analytics
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Track performance metrics and gain insights into your support operations.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Typography variant="h5" gutterBottom>Available Reports</Typography>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              📊 Agent Performance
            </Typography>
            <Typography paragraph>
              Track individual agent metrics:
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="• Conversations handled" /></ListItem>
              <ListItem><ListItemText primary="• Messages sent" /></ListItem>
              <ListItem><ListItemText primary="• Average response time" /></ListItem>
              <ListItem><ListItemText primary="• Resolution rate" /></ListItem>
              <ListItem><ListItemText primary="• CSAT scores" /></ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              👥 Team Performance
            </Typography>
            <Typography paragraph>
              Compare team efficiency:
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="• Team workload distribution" /></ListItem>
              <ListItem><ListItemText primary="• Efficiency scores" /></ListItem>
              <ListItem><ListItemText primary="• Collaboration index" /></ListItem>
              <ListItem><ListItemText primary="• SLA compliance" /></ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              ⏱️ SLA Compliance
            </Typography>
            <Typography paragraph>
              Monitor service level agreements:
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="• First response time" /></ListItem>
              <ListItem><ListItemText primary="• Resolution time" /></ListItem>
              <ListItem><ListItemText primary="• SLA breaches" /></ListItem>
              <ListItem><ListItemText primary="• Compliance trends" /></ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              😊 Customer Satisfaction (CSAT)
            </Typography>
            <Typography paragraph>
              Measure customer happiness:
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="• Overall CSAT score" /></ListItem>
              <ListItem><ListItemText primary="• Ratings distribution" /></ListItem>
              <ListItem><ListItemText primary="• Customer feedback" /></ListItem>
              <ListItem><ListItemText primary="• Trends over time" /></ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              💬 Conversation Quality
            </Typography>
            <Typography paragraph>
              Analyze conversation quality:
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="• Quality scores (A-F grades)" /></ListItem>
              <ListItem><ListItemText primary="• Completeness metrics" /></ListItem>
              <ListItem><ListItemText primary="• Tone analysis" /></ListItem>
              <ListItem><ListItemText primary="• Improvement areas" /></ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              🎭 Sentiment Analysis
            </Typography>
            <Typography paragraph>
              Understand customer emotions:
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="• Positive/Negative/Neutral breakdown" /></ListItem>
              <ListItem><ListItemText primary="• Sentiment trends" /></ListItem>
              <ListItem><ListItemText primary="• Agent-wise sentiment scores" /></ListItem>
              <ListItem><ListItemText primary="• Issue identification" /></ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Custom Date Ranges</Typography>
      <Typography paragraph>
        All reports support custom date range filtering:
      </Typography>
      <List>
        <ListItem>
          <ListItemIcon><Info color="primary" /></ListItemIcon>
          <ListItemText
            primary="Quick Filters"
            secondary="Today, Yesterday, Last 7 days, Last 30 days, This month, Last month"
          />
        </ListItem>
        <ListItem>
          <ListItemIcon><Info color="primary" /></ListItemIcon>
          <ListItemText
            primary="Custom Range"
            secondary="Select any start and end date for precise analysis"
          />
        </ListItem>
      </List>
    </Box>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Exporting Reports</Typography>
      <Typography paragraph>
        Export reports in multiple formats:
      </Typography>
      <Grid container spacing={2}>
        {['CSV', 'PDF', 'Excel'].map((format) => (
          <Grid item key={format}>
            <Chip label={format} color="primary" variant="outlined" />
          </Grid>
        ))}
      </Grid>
    </Box>

    <Alert severity="info" sx={{ mt: 3 }}>
      <strong>Pro Tip:</strong> Schedule automated report emails at Settings → Notifications → Report Schedules to receive regular performance updates.
    </Alert>
  </Box>
);

const TeamsContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      Team Management
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Organize your support team for maximum efficiency.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Adding Team Members</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Navigate to <strong>Settings → Teams → Add Agent</strong>
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="1. Enter agent email address"
              secondary="They'll receive an invitation email"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="2. Assign role"
              secondary="Choose between Administrator, Agent, or Viewer"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3. Select teams"
              secondary="Add them to one or more teams"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="4. Set permissions"
              secondary="Configure specific feature access (optional)"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Creating Teams</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Organize agents into teams based on:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem><ListItemText primary="• Channel (WhatsApp Team, Email Team)" /></ListItem>
              <ListItem><ListItemText primary="• Product line (Product A, Product B)" /></ListItem>
              <ListItem><ListItemText primary="• Language (English, Spanish, French)" /></ListItem>
            </List>
          </Grid>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem><ListItemText primary="• Region (US, EU, APAC)" /></ListItem>
              <ListItem><ListItemText primary="• Skill level (Tier 1, Tier 2, Tier 3)" /></ListItem>
              <ListItem><ListItemText primary="• Time zones (EST, PST, GMT)" /></ListItem>
            </List>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Agent Roles & Permissions</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>Available roles in Converse:</Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>👑 Administrator</Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
            <List dense>
              <ListItem><ListItemText primary="✓ Full access to all features" /></ListItem>
              <ListItem><ListItemText primary="✓ Manage team members and settings" /></ListItem>
              <ListItem><ListItemText primary="✓ Configure channels and integrations" /></ListItem>
              <ListItem><ListItemText primary="✓ Access all reports and analytics" /></ListItem>
              <ListItem><ListItemText primary="✓ Billing and subscription management" /></ListItem>
            </List>
          </Card>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>👤 Agent</Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
            <List dense>
              <ListItem><ListItemText primary="✓ Handle assigned conversations" /></ListItem>
              <ListItem><ListItemText primary="✓ Access knowledge base" /></ListItem>
              <ListItem><ListItemText primary="✓ Use canned responses" /></ListItem>
              <ListItem><ListItemText primary="✓ View personal reports" /></ListItem>
              <ListItem><ListItemText primary="✗ Cannot modify settings" /></ListItem>
            </List>
          </Card>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>👁️ Viewer</Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
            <List dense>
              <ListItem><ListItemText primary="✓ Read-only access to conversations" /></ListItem>
              <ListItem><ListItemText primary="✓ View reports and dashboards" /></ListItem>
              <ListItem><ListItemText primary="✗ Cannot send messages" /></ListItem>
              <ListItem><ListItemText primary="✗ Cannot modify conversations" /></ListItem>
            </List>
          </Card>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Agent Capacity Management</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Control how many conversations each agent can handle:
        </Typography>
        <Typography paragraph>
          Go to <strong>Settings → Agent Capacity</strong> to:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Set Max Conversations"
              secondary="Limit concurrent conversations per agent"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Auto-Assignment Rules"
              secondary="Round-robin, load-based, or skill-based routing"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Availability Status"
              secondary="Agents can mark themselves as Available, Busy, or Away"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Alert severity="success" sx={{ mt: 3 }}>
      <strong>Best Practice:</strong> Create teams aligned with your customer journey. For example, Sales Team for pre-sales, Support Team for technical issues, and Success Team for onboarding.
    </Alert>
  </Box>
);

const APIContent: React.FC = () => (
  <Box>
    <Typography variant="h4" fontWeight="bold" gutterBottom>
      API & Integrations
    </Typography>
    <Typography variant="body1" paragraph color="text.secondary">
      Build custom integrations using Converse REST API.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Alert severity="info" sx={{ mb: 3 }}>
      <strong>API Base URL:</strong> <code>https://api.converse.ai/v1</code> (or your self-hosted URL)
    </Alert>

    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Authentication</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Converse API uses token-based authentication.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>Getting an API Token:</Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="1. Go to Settings → API Tokens"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="2. Click 'Create New Token'"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3. Give it a name and set expiry (optional)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="4. Copy the token (it won't be shown again!)"
            />
          </ListItem>
        </List>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Using the Token:</Typography>
        <Card variant="outlined" sx={{ p: 2, bgcolor: '#1e1e1e', color: '#fff', mt: 1 }}>
          <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
{`curl -X GET \\
  https://api.converse.ai/v1/conversations \\
  -H "Authorization: Bearer YOUR_API_TOKEN"`}
          </Typography>
        </Card>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Common API Endpoints</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="subtitle2" gutterBottom>Conversations:</Typography>
        <List dense sx={{ mb: 2 }}>
          <ListItem>
            <ListItemText
              primary={<code>GET /conversations</code>}
              secondary="List all conversations"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<code>POST /conversations</code>}
              secondary="Create a new conversation"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<code>GET /conversations/:id</code>}
              secondary="Get conversation details"
            />
          </ListItem>
        </List>

        <Typography variant="subtitle2" gutterBottom>Messages:</Typography>
        <List dense sx={{ mb: 2 }}>
          <ListItem>
            <ListItemText
              primary={<code>POST /conversations/:id/messages</code>}
              secondary="Send a message"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<code>GET /conversations/:id/messages</code>}
              secondary="Get conversation messages"
            />
          </ListItem>
        </List>

        <Typography variant="subtitle2" gutterBottom>Contacts:</Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary={<code>GET /contacts</code>}
              secondary="List all contacts"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<code>POST /contacts</code>}
              secondary="Create a contact"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<code>GET /contacts/:id</code>}
              secondary="Get contact details"
            />
          </ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Webhooks</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Receive real-time notifications when events occur.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>Setting up Webhooks:</Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="1. Go to Settings → Webhooks → Add Webhook"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="2. Enter your endpoint URL"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3. Select events to subscribe to"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="4. Test the webhook"
            />
          </ListItem>
        </List>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Available Events:</Typography>
        <Grid container spacing={1}>
          {[
            'conversation.created', 'conversation.updated', 'conversation.resolved',
            'message.created', 'message.updated',
            'contact.created', 'contact.updated'
          ].map((event) => (
            <Grid item key={event}>
              <Chip label={event} size="small" variant="outlined" />
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">SDKs & Libraries</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Official SDKs available for popular languages:
        </Typography>
        <Grid container spacing={2}>
          {[
            { name: 'JavaScript/Node.js', badge: 'npm install @converse/sdk' },
            { name: 'Python', badge: 'pip install converse-sdk' },
            { name: 'Ruby', badge: 'gem install converse' },
            { name: 'PHP', badge: 'composer require converse/sdk' }
          ].map((sdk) => (
            <Grid item xs={12} sm={6} key={sdk.name}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>{sdk.name}</Typography>
                  <Chip label={sdk.badge} size="small" sx={{ fontFamily: 'monospace' }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Rate Limits</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          API rate limits to ensure fair usage:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Standard Plan: 1000 requests/hour"
              secondary="Sufficient for most integrations"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Pro Plan: 5000 requests/hour"
              secondary="For high-volume integrations"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Enterprise: Custom limits"
              secondary="Contact sales for custom rate limits"
            />
          </ListItem>
        </List>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Rate limit headers are included in every response. Monitor <code>X-RateLimit-Remaining</code> to avoid throttling.
        </Alert>
      </AccordionDetails>
    </Accordion>

    <Box sx={{ mt: 4, p: 3, bgcolor: '#f0f9ff', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Need Help?
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary="📚 Full API Documentation"
            secondary="View at /api/docs or contact support"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="💬 Developer Community"
            secondary="Join our Discord for API discussions"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="🐛 Report Issues"
            secondary="Submit bugs via Settings → Help & Support"
          />
        </ListItem>
      </List>
    </Box>
  </Box>
);

export default HelpPage;
